import { Point, HighlightArea, Wall, Room, WallWithTempId } from '../types';
import { pointToLineDistance, isNearPoint, distanceToLine, calculateRooms } from './floorPlanUtils';

// Default highlight colors
export const HIGHLIGHT_COLORS = {
  YELLOW: '#FFFF00',
  ORANGE: '#FFA500',
  PINK: '#FF69B4',
  GREEN: '#00FF00',
  BLUE: '#00BFFF',
  RED: '#FF0000',
  PURPLE: '#9370DB'
} as const;

// Wall snapping configuration for highlights
const HIGHLIGHT_SNAP_DISTANCE = 30; // pixels - distance to snap to walls
const WALL_EDGE_SNAP_DISTANCE = 20; // pixels - distance to snap along wall edges
const ROOM_BOUNDARY_SNAP_DISTANCE = 25; // pixels - distance to snap to room boundaries
const AREA_INTERSECTION_TOLERANCE = 5; // pixels - tolerance for area intersection detection

export interface HighlightWallSnap {
  snappedPoint: Point;
  snapType: 'wall-edge' | 'wall-endpoint' | 'room-boundary' | 'none';
  snappedWall?: Wall | WallWithTempId;
  snappedRoom?: Room;
}

export interface HighlightAreaSnap {
  snappedStartPoint: Point;
  snappedEndPoint: Point;
  snapType: 'wall-section' | 'room-area' | 'wall-endpoints' | 'none';
  snappedWalls: (Wall | WallWithTempId)[];
  snappedRooms: Room[];
}

/**
 * Checks if a line segment intersects with another line segment
 */
function doLinesIntersect(
  p1: Point, q1: Point, // First line segment
  p2: Point, q2: Point  // Second line segment
): boolean {
  function orientation(p: Point, q: Point, r: Point): number {
    const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
    if (val === 0) return 0;  // collinear
    return (val > 0) ? 1 : 2; // clockwise or counterclockwise
  }

  function onSegment(p: Point, q: Point, r: Point): boolean {
    return q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x) &&
           q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y);
  }

  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);

  // General case
  if (o1 !== o2 && o3 !== o4) return true;

  // Special cases for collinear points
  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, q2, q1)) return true;
  if (o3 === 0 && onSegment(p2, p1, q2)) return true;
  if (o4 === 0 && onSegment(p2, q1, q2)) return true;

  return false;
}

/**
 * Checks if a rectangle intersects with a wall line
 */
function doesRectangleIntersectWall(
  rectStart: Point,
  rectEnd: Point,
  wall: Wall | WallWithTempId,
  tolerance: number = AREA_INTERSECTION_TOLERANCE
): boolean {
  // Create rectangle bounds
  const minX = Math.min(rectStart.x, rectEnd.x) - tolerance;
  const maxX = Math.max(rectStart.x, rectEnd.x) + tolerance;
  const minY = Math.min(rectStart.y, rectEnd.y) - tolerance;
  const maxY = Math.max(rectStart.y, rectEnd.y) + tolerance;

  // Check if wall endpoints are inside rectangle
  if ((wall.start.x >= minX && wall.start.x <= maxX && wall.start.y >= minY && wall.start.y <= maxY) ||
      (wall.end.x >= minX && wall.end.x <= maxX && wall.end.y >= minY && wall.end.y <= maxY)) {
    return true;
  }

  // Check if wall intersects with any rectangle edge
  const rectCorners = [
    { x: minX, y: minY }, // top-left
    { x: maxX, y: minY }, // top-right
    { x: maxX, y: maxY }, // bottom-right
    { x: minX, y: maxY }  // bottom-left
  ];

  // Check each edge of the rectangle
  for (let i = 0; i < rectCorners.length; i++) {
    const edgeStart = rectCorners[i];
    const edgeEnd = rectCorners[(i + 1) % rectCorners.length];
    
    if (doLinesIntersect(edgeStart, edgeEnd, wall.start, wall.end)) {
      return true;
    }
  }

  // Check if rectangle is inside wall (wall crosses through rectangle)
  const rectCenter = {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2
  };
  
  const distToWall = pointToLineDistance(rectCenter, wall.start, wall.end);
  const rectDiagonal = Math.sqrt((maxX - minX) ** 2 + (maxY - minY) ** 2) / 2;
  
  return distToWall <= rectDiagonal + tolerance;
}

/**
 * Snaps a highlight area to walls based on rectangle intersection
 * Now constrains selection to room boundaries only
 */
export function snapHighlightAreaToWalls(
  startPoint: Point,
  endPoint: Point,
  walls: (Wall | WallWithTempId)[],
  rooms: Room[],
  currentFloor: number
): HighlightAreaSnap {
  // First, constrain the highlight to room boundaries only
  const roomConstraint = constrainHighlightToRoomBoundaries(startPoint, endPoint, rooms);
  
  if (!roomConstraint.isFullyConstrained) {
    // If highlight extends outside room boundaries, return original with warning
    console.log('⚠️ Highlight extends outside room boundaries, no snapping applied');
    return {
      snappedStartPoint: startPoint,
      snappedEndPoint: endPoint,
      snapType: 'none',
      snappedWalls: [],
      snappedRooms: []
    };
  }
  
  // Use the constrained points for further processing
  const constrainedStart = roomConstraint.constrainedStart;
  const constrainedEnd = roomConstraint.constrainedEnd;
  
  // Filter walls for current floor
  const currentFloorWalls = walls.filter(wall => 
    (wall as any).floor === currentFloor || (wall as any).floor === undefined
  );

  const intersectingWalls: (Wall | WallWithTempId)[] = [];
  const intersectingRooms: Room[] = [];

  // Find all walls that intersect with the constrained highlight rectangle
  for (const wall of currentFloorWalls) {
    if (doesRectangleIntersectWall(constrainedStart, constrainedEnd, wall)) {
      intersectingWalls.push(wall);
    }
  }

  // Find rooms that the constrained highlight intersects with
  for (const room of rooms) {
    if (!room.walls || room.walls.length === 0) continue;
    
    for (const roomWall of room.walls) {
      if (doesRectangleIntersectWall(constrainedStart, constrainedEnd, roomWall)) {
        if (!intersectingRooms.includes(room)) {
          intersectingRooms.push(room);
        }
        // Also add room walls to intersecting walls if not already there
        const wallExists = intersectingWalls.some(w => 
          (Math.abs(w.start.x - roomWall.start.x) < 0.1 && 
           Math.abs(w.start.y - roomWall.start.y) < 0.1 &&
           Math.abs(w.end.x - roomWall.end.x) < 0.1 && 
           Math.abs(w.end.y - roomWall.end.y) < 0.1)
        );
        if (!wallExists) {
          intersectingWalls.push(roomWall);
        }
        break; // Only need one wall per room for room detection
      }
    }
  }

  console.log('🔍 CONSTRAINED AREA INTERSECTION ANALYSIS:', {
    originalStart: startPoint,
    originalEnd: endPoint,
    constrainedStart,
    constrainedEnd,
    intersectingWallsCount: intersectingWalls.length,
    intersectingRoomsCount: intersectingRooms.length,
    wallDetails: intersectingWalls.map(w => ({
      start: w.start,
      end: w.end,
      type: (w as any).type || 'unknown'
    }))
  });

  // Return the constrained area (no expansion beyond room boundaries)
  const snappedStart = constrainedStart;
  const snappedEnd = constrainedEnd;

  console.log('🎯 ROOM-CONSTRAINED AREA SNAPPING APPLIED:', {
    originalBounds: {
      minX: Math.min(startPoint.x, endPoint.x),
      maxX: Math.max(startPoint.x, endPoint.x),
      minY: Math.min(startPoint.y, endPoint.y),
      maxY: Math.max(startPoint.y, endPoint.y)
    },
    constrainedBounds: { 
      minX: constrainedStart.x, 
      maxX: constrainedEnd.x, 
      minY: constrainedStart.y, 
      maxY: constrainedEnd.y 
    },
    snappedStart,
    snappedEnd,
    wallsIncluded: intersectingWalls.length,
    roomsIncluded: intersectingRooms.length
  });

  return {
    snappedStartPoint: snappedStart,
    snappedEndPoint: snappedEnd,
    snapType: intersectingRooms.length > 0 ? 'room-area' : 'wall-section',
    snappedWalls: intersectingWalls,
    snappedRooms: intersectingRooms
  };
}

/**
 * Constrains a highlight area to room boundaries only
 */
function constrainHighlightToRoomBoundaries(
  startPoint: Point,
  endPoint: Point,
  rooms: Room[]
): { 
  constrainedStart: Point; 
  constrainedEnd: Point; 
  isFullyConstrained: boolean;
} {
  // Get the original rectangle bounds
  const minX = Math.min(startPoint.x, endPoint.x);
  const maxX = Math.max(startPoint.x, endPoint.x);
  const minY = Math.min(startPoint.y, endPoint.y);
  const maxY = Math.max(startPoint.y, endPoint.y);
  
  // Find the bounding box of all rooms that the highlight intersects
  let roomBoundsMinX = Infinity;
  let roomBoundsMaxX = -Infinity;
  let roomBoundsMinY = Infinity;
  let roomBoundsMaxY = -Infinity;
  
  let hasIntersectingRooms = false;
  
  for (const room of rooms) {
    if (!room.walls || room.walls.length === 0) continue;
    
    // Check if the highlight intersects with this room
    let roomIntersects = false;
    
    // Get room bounds
    let roomMinX = Infinity;
    let roomMaxX = -Infinity;
    let roomMinY = Infinity;
    let roomMaxY = -Infinity;
    
    for (const wall of room.walls) {
      roomMinX = Math.min(roomMinX, wall.start.x, wall.end.x);
      roomMaxX = Math.max(roomMaxX, wall.start.x, wall.end.x);
      roomMinY = Math.min(roomMinY, wall.start.y, wall.end.y);
      roomMaxY = Math.max(roomMaxY, wall.start.y, wall.end.y);
    }
    
    // Check if highlight rectangle intersects with room bounds
    if (!(maxX < roomMinX || minX > roomMaxX || maxY < roomMinY || minY > roomMaxY)) {
      roomIntersects = true;
    }
    
    if (roomIntersects) {
      hasIntersectingRooms = true;
      roomBoundsMinX = Math.min(roomBoundsMinX, roomMinX);
      roomBoundsMaxX = Math.max(roomBoundsMaxX, roomMaxX);
      roomBoundsMinY = Math.min(roomBoundsMinY, roomMinY);
      roomBoundsMaxY = Math.max(roomBoundsMaxY, roomMaxY);
    }
  }
  
  if (!hasIntersectingRooms) {
    // No rooms intersect, return original points
    return {
      constrainedStart: startPoint,
      constrainedEnd: endPoint,
      isFullyConstrained: false
    };
  }
  
  // Constrain the highlight to room boundaries
  const constrainedMinX = Math.max(minX, roomBoundsMinX);
  const constrainedMaxX = Math.min(maxX, roomBoundsMaxX);
  const constrainedMinY = Math.max(minY, roomBoundsMinY);
  const constrainedMaxY = Math.min(maxY, roomBoundsMaxY);
  
  const constrainedStart = { x: constrainedMinX, y: constrainedMinY };
  const constrainedEnd = { x: constrainedMaxX, y: constrainedMaxY };
  
  // Check if the constrained area is still valid
  const isFullyConstrained = (constrainedMaxX > constrainedMinX && constrainedMaxY > constrainedMinY);
  
  console.log('🏠 ROOM BOUNDARY CONSTRAINT:', {
    original: { minX, maxX, minY, maxY },
    constrained: { 
      minX: constrainedMinX, 
      maxX: constrainedMaxX, 
      minY: constrainedMinY, 
      maxY: constrainedMaxY 
    },
    isFullyConstrained,
    intersectingRooms: hasIntersectingRooms
  });
  
  return {
    constrainedStart,
    constrainedEnd,
    isFullyConstrained
  };
}

/**
 * Snaps a highlight point to nearby walls and room boundaries
 */
export function snapHighlightToWalls(
  point: Point,
  walls: (Wall | WallWithTempId)[],
  rooms: Room[],
  currentFloor: number
): HighlightWallSnap {
  // Filter walls for current floor
  const currentFloorWalls = walls.filter(wall => 
    (wall as any).floor === currentFloor || (wall as any).floor === undefined
  );
  
  let bestSnap: HighlightWallSnap = {
    snappedPoint: point,
    snapType: 'none'
  };
  let minDistance = Infinity;
  
  // 1. Check for wall endpoint snapping (highest priority)
  for (const wall of currentFloorWalls) {
    // Check start point
    const startDistance = Math.hypot(point.x - wall.start.x, point.y - wall.start.y);
    if (startDistance < HIGHLIGHT_SNAP_DISTANCE && startDistance < minDistance) {
      bestSnap = {
        snappedPoint: { ...wall.start },
        snapType: 'wall-endpoint',
        snappedWall: wall
      };
      minDistance = startDistance;
    }
    
    // Check end point
    const endDistance = Math.hypot(point.x - wall.end.x, point.y - wall.end.y);
    if (endDistance < HIGHLIGHT_SNAP_DISTANCE && endDistance < minDistance) {
      bestSnap = {
        snappedPoint: { ...wall.end },
        snapType: 'wall-endpoint',
        snappedWall: wall
      };
      minDistance = endDistance;
    }
  }
  
  // 2. Check for wall edge snapping (medium priority)
  for (const wall of currentFloorWalls) {
    const distanceToWall = pointToLineDistance(point, wall.start, wall.end);
    if (distanceToWall < WALL_EDGE_SNAP_DISTANCE && distanceToWall < minDistance) {
      // Find closest point on wall edge
      const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
      const wallLength = Math.sqrt(wallVector.x ** 2 + wallVector.y ** 2);
      
      if (wallLength > 0) {
        const pointVector = { x: point.x - wall.start.x, y: point.y - wall.start.y };
        const projection = (pointVector.x * wallVector.x + pointVector.y * wallVector.y) / (wallLength ** 2);
        
        // Only snap if projection is within the wall segment
        if (projection >= 0 && projection <= 1) {
          const snapPoint = {
            x: wall.start.x + projection * wallVector.x,
            y: wall.start.y + projection * wallVector.y
          };
          
          bestSnap = {
            snappedPoint: snapPoint,
            snapType: 'wall-edge',
            snappedWall: wall
          };
          minDistance = distanceToWall;
        }
      }
    }
  }
  
  // 3. Check for room boundary snapping (lowest priority)
  for (const room of rooms) {
    if (!room.walls || room.walls.length === 0) continue;
    
    for (const roomWall of room.walls) {
      const distanceToRoomWall = pointToLineDistance(point, roomWall.start, roomWall.end);
      if (distanceToRoomWall < ROOM_BOUNDARY_SNAP_DISTANCE && distanceToRoomWall < minDistance) {
        // Find closest point on room boundary
        const wallVector = { x: roomWall.end.x - roomWall.start.x, y: roomWall.end.y - roomWall.start.y };
        const wallLength = Math.sqrt(wallVector.x ** 2 + wallVector.y ** 2);
        
        if (wallLength > 0) {
          const pointVector = { x: point.x - roomWall.start.x, y: point.y - roomWall.start.y };
          const projection = (pointVector.x * wallVector.x + pointVector.y * wallVector.y) / (wallLength ** 2);
          
          // Only snap if projection is within the wall segment
          if (projection >= 0 && projection <= 1) {
            const snapPoint = {
              x: roomWall.start.x + projection * wallVector.x,
              y: roomWall.start.y + projection * wallVector.y
            };
            
            bestSnap = {
              snappedPoint: snapPoint,
              snapType: 'room-boundary',
              snappedRoom: room
            };
            minDistance = distanceToRoomWall;
          }
        }
      }
    }
  }
  
  return bestSnap;
}

/**
 * Creates a highlight area with wall snapping applied to both start and end points
 */
export function createHighlightAreaWithWallSnapping(
  startPoint: Point,
  endPoint: Point,
  walls: (Wall | WallWithTempId)[],
  rooms: Room[],
  floor: number,
  color: string = HIGHLIGHT_COLORS.ORANGE,
  opacity: number = 0.3
): HighlightArea {
  // Apply area-based wall snapping instead of point-based snapping
  const areaSnap = snapHighlightAreaToWalls(startPoint, endPoint, walls, rooms, floor);
  
  console.log('🎯 HIGHLIGHT AREA WALL SNAPPING:', {
    originalStart: startPoint,
    originalEnd: endPoint,
    snappedStart: areaSnap.snappedStartPoint,
    snappedEnd: areaSnap.snappedEndPoint,
    snapType: areaSnap.snapType,
    wallsDetected: areaSnap.snappedWalls.length,
    roomsDetected: areaSnap.snappedRooms.length
  });
  
  return {
    id: `highlight_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type: 'highlight',
    startPoint: areaSnap.snappedStartPoint,
    endPoint: areaSnap.snappedEndPoint,
    floor,
    color,
    opacity,
    savedToNotes: false
  };
}

/**
 * Creates a preview highlight area with wall snapping
 */
export function createHighlightPreviewWithWallSnapping(
  startPoint: Point,
  currentPoint: Point,
  walls: (Wall | WallWithTempId)[],
  rooms: Room[],
  floor: number,
  color: string = HIGHLIGHT_COLORS.ORANGE,
  opacity: number = 0.3
): HighlightArea {
  // Apply area-based wall snapping for the preview
  const areaSnap = snapHighlightAreaToWalls(startPoint, currentPoint, walls, rooms, floor);
  
  return {
    id: 'highlight_preview',
    type: 'highlight',
    startPoint: areaSnap.snappedStartPoint,
    endPoint: areaSnap.snappedEndPoint,
    floor,
    color,
    opacity: opacity * 0.7, // Make preview slightly more transparent
    savedToNotes: false
  };
}

/**
 * Creates a new highlight area from start and end points (original function without wall snapping)
 */
export function createHighlightArea(
  startPoint: Point,
  endPoint: Point,
  floor: number,
  color: string = HIGHLIGHT_COLORS.ORANGE,
  opacity: number = 0.3
): HighlightArea {
  return {
    id: `highlight_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type: 'highlight',
    startPoint,
    endPoint,
    floor,
    color,
    opacity,
    savedToNotes: false
  };
}

/**
 * Creates a preview highlight area while dragging (original function without wall snapping)
 */
export function createHighlightPreview(
  startPoint: Point,
  currentPoint: Point,
  floor: number,
  color: string = HIGHLIGHT_COLORS.ORANGE,
  opacity: number = 0.3
): HighlightArea {
  return {
    id: 'highlight_preview',
    type: 'highlight',
    startPoint,
    endPoint: currentPoint,
    floor,
    color,
    opacity,
    savedToNotes: false
  };
}

/**
 * Draws a highlight area on the canvas
 */
export function drawHighlightArea(
  ctx: CanvasRenderingContext2D,
  highlight: HighlightArea,
  viewOffset: Point,
  scale: number
): void {
  // Use coordinates directly since canvas context is already transformed
  const startX = highlight.startPoint.x;
  const startY = highlight.startPoint.y;
  const endX = highlight.endPoint.x;
  const endY = highlight.endPoint.y;

  // Calculate rectangle dimensions
  const width = Math.abs(endX - startX);
  const height = Math.abs(endY - startY);
  const rectX = Math.min(startX, endX);
  const rectY = Math.min(startY, endY);

  // Save current context state
  ctx.save();

  // Draw filled rectangle
  ctx.globalAlpha = highlight.opacity;
  ctx.fillStyle = highlight.color;
  ctx.fillRect(rectX, rectY, width, height);

  // Draw border - orange solid border for all highlights, positioned inside to avoid obscuring walls
  ctx.globalAlpha = 1; // Full opacity for border
  ctx.lineWidth = 2 / scale; // Scale line width for consistent appearance
  
  // Inset the border slightly inside the highlight area to avoid covering wall colors
  const borderInset = 3 / scale; // 3px inset scaled appropriately
  const borderRectX = rectX + borderInset;
  const borderRectY = rectY + borderInset;
  const borderWidth = width - (borderInset * 2);
  const borderHeight = height - (borderInset * 2);
  
  // Use orange border for all highlights to distinguish extensions
  ctx.strokeStyle = '#f97316'; // Orange color for all highlights
  ctx.setLineDash([]); // Solid line
  ctx.strokeRect(borderRectX, borderRectY, borderWidth, borderHeight);
  
  // Add a subtle orange glow effect for better visibility
  ctx.shadowColor = '#f97316';
  ctx.shadowBlur = 4 / scale;
  ctx.strokeRect(borderRectX, borderRectY, borderWidth, borderHeight);
  ctx.shadowBlur = 0; // Reset shadow
  
  ctx.setLineDash([]); // Reset line dash

  // Restore context state
  ctx.restore();
}

/**
 * Draws all highlight areas for the current floor
 */
export function drawHighlightAreas(
  ctx: CanvasRenderingContext2D,
  highlights: HighlightArea[],
  currentFloor: number,
  viewOffset: Point,
  scale: number
): void {
  highlights
    .filter(highlight => highlight.floor === currentFloor)
    .forEach(highlight => {
      drawHighlightArea(ctx, highlight, viewOffset, scale);
    });
}

/**
 * Draws a highlight preview while dragging
 */
export function drawHighlightPreview(
  ctx: CanvasRenderingContext2D,
  highlightPreview: HighlightArea | null,
  viewOffset: Point,
  scale: number
): void {
  if (!highlightPreview) return;
  
  // Draw with slightly different styling to indicate it's a preview
  const previewHighlight = {
    ...highlightPreview,
    opacity: highlightPreview.opacity * 0.7 // Make preview more transparent
  };
  
  drawHighlightArea(ctx, previewHighlight, viewOffset, scale);
  
  // 🆕 ADD DIMENSIONS: Show width and length in preview mode
  drawHighlightDimensions(ctx, highlightPreview, scale);
}

/**
 * Draws visual indicators for wall snapping during highlight preview
 */
export function drawHighlightSnapIndicators(
  ctx: CanvasRenderingContext2D,
  areaSnap: HighlightAreaSnap,
  viewOffset: Point,
  scale: number
): void {
  if (!areaSnap || areaSnap.snapType === 'none' || areaSnap.snappedWalls.length === 0) {
    return;
  }

  ctx.save();
  
  // Draw indicators for snapped walls
  ctx.strokeStyle = '#00FF00'; // Bright green for snap indicators
  ctx.lineWidth = 3 / scale; // Thicker line for visibility
  ctx.setLineDash([8 / scale, 4 / scale]); // Scale dash pattern for consistent appearance
  ctx.globalAlpha = 0.8;

  for (const wall of areaSnap.snappedWalls) {
    // Use wall coordinates directly since canvas context is already transformed
    const startX = wall.start.x;
    const startY = wall.start.y;
    const endX = wall.end.x;
    const endY = wall.end.y;

    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    // Draw small circles at wall endpoints to show they're detected
    ctx.fillStyle = '#00FF00';
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(startX, startY, 4 / scale, 0, Math.PI * 2); // Scale circle size
    ctx.fill();
    ctx.beginPath();
    ctx.arc(endX, endY, 4 / scale, 0, Math.PI * 2); // Scale circle size
    ctx.fill();
    ctx.globalAlpha = 0.8;
  }

  ctx.restore();
}

/**
 * Enhanced preview drawing with snap indicators
 */
export function drawHighlightPreviewWithSnapIndicators(
  ctx: CanvasRenderingContext2D,
  highlightPreview: HighlightArea | null,
  areaSnap: HighlightAreaSnap | null,
  viewOffset: Point,
  scale: number
): void {
  if (!highlightPreview) return;
  
  // Draw the regular highlight preview (without dimensions, we'll add them after)
  const previewHighlight = {
    ...highlightPreview,
    opacity: highlightPreview.opacity * 0.7 // Make preview more transparent
  };
  
  drawHighlightArea(ctx, previewHighlight, viewOffset, scale);
  
  // Draw snap indicators if snapping is active
  if (areaSnap && areaSnap.snapType !== 'none') {
    drawHighlightSnapIndicators(ctx, areaSnap, viewOffset, scale);
    
    // Add a subtle glow effect around the preview when snapping
    ctx.save();
    ctx.shadowColor = '#00FF00';
    ctx.shadowBlur = 10;
    ctx.globalAlpha = 0.3;
    drawHighlightArea(ctx, highlightPreview, viewOffset, scale);
    ctx.restore();
  }
  
  // 🆕 ADD DIMENSIONS: Show width and length in preview mode
  drawHighlightDimensions(ctx, highlightPreview, scale);
}

/**
 * Checks if a point is within a highlight area
 */
export function isPointInHighlight(point: Point, highlight: HighlightArea): boolean {
  const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
  const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
  const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
  const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);

  return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
}

/**
 * Finds a highlight area at a given point
 */
export function findHighlightAtPoint(
  point: Point,
  highlights: HighlightArea[],
  currentFloor: number
): HighlightArea | null {
  // Check highlights on current floor, in reverse order (top to bottom)
  const floorHighlights = highlights
    .filter(highlight => highlight.floor === currentFloor)
    .reverse();

  for (const highlight of floorHighlights) {
    if (isPointInHighlight(point, highlight)) {
      return highlight;
    }
  }

  return null;
}

/**
 * Gets the area of a highlight in square units
 */
export function getHighlightArea(highlight: HighlightArea): number {
  const width = Math.abs(highlight.endPoint.x - highlight.startPoint.x);
  const height = Math.abs(highlight.endPoint.y - highlight.startPoint.y);
  return width * height;
}

/**
 * Draws green borders around walls for submitted highlights
 */
export function drawSubmittedHighlightBorders(
  ctx: CanvasRenderingContext2D,
  submittedHighlights: Array<{
    highlight: HighlightArea;
    floorLevel: string;
    buildingType: string;
    calculations: { area: number; hlp: number; pw: number; sw: number };
  }>,
  currentFloor: number,
  walls: (Wall | WallWithTempId)[],
  viewOffset: Point,
  scale: number
): void {
  if (submittedHighlights.length === 0) return;

  // Filter submitted highlights for current floor
  const currentFloorHighlights = submittedHighlights.filter(
    sh => sh.highlight.floor === currentFloor
  );

  if (currentFloorHighlights.length === 0) return;

  ctx.save();
  
  // Set green border style
  ctx.strokeStyle = '#10B981'; // Green-500
  ctx.lineWidth = 3 / scale; // Scale line width for consistent appearance
  ctx.setLineDash([]);
  ctx.globalAlpha = 0.8;

  for (const submittedHighlight of currentFloorHighlights) {
    const { highlight } = submittedHighlight;
    
    // Find walls that intersect with this highlight area
    const intersectingWalls = walls.filter(wall => 
      doesWallIntersectHighlight(wall, highlight)
    );

    // Draw green borders around intersecting walls
    for (const wall of intersectingWalls) {
      ctx.beginPath();
      ctx.moveTo(wall.start.x, wall.start.y);
      ctx.lineTo(wall.end.x, wall.end.y);
      ctx.stroke();

      // Draw small green circles at wall endpoints for better visibility
      ctx.fillStyle = '#10B981';
      ctx.beginPath();
      ctx.arc(wall.start.x, wall.start.y, 4 / scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(wall.end.x, wall.end.y, 4 / scale, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw a subtle overlay to show the selected area bounds
    const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
    const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
    const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
    const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);

    // Draw a subtle green overlay
    ctx.fillStyle = 'rgba(16, 185, 129, 0.1)'; // Green with low opacity
    ctx.fillRect(minX, minY, maxX - minX, maxY - minY);

    // Draw a dashed border around the area
    ctx.strokeStyle = '#10B981';
    ctx.lineWidth = 2 / scale;
    ctx.setLineDash([8 / scale, 4 / scale]);
    ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
  }

  ctx.restore();
}

/**
 * Helper function to check if a wall intersects with a highlight area (reused from calculations)
 */
function doesWallIntersectHighlight(wall: Wall | WallWithTempId, highlight: HighlightArea): boolean {
  const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
  const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
  const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
  const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);

  // Check if wall endpoints are within the highlight area
  const startInside = (wall.start.x >= minX && wall.start.x <= maxX && 
                      wall.start.y >= minY && wall.start.y <= maxY);
  const endInside = (wall.end.x >= minX && wall.end.x <= maxX && 
                    wall.end.y >= minY && wall.end.y <= maxY);

  if (startInside || endInside) {
    return true;
  }

  // Check if the wall line intersects with any of the highlight rectangle edges
  const wallStart = wall.start;
  const wallEnd = wall.end;
  
  // Define rectangle edges
  const rectEdges = [
    { start: { x: minX, y: minY }, end: { x: maxX, y: minY } }, // top
    { start: { x: maxX, y: minY }, end: { x: maxX, y: maxY } }, // right
    { start: { x: maxX, y: maxY }, end: { x: minX, y: maxY } }, // bottom
    { start: { x: minX, y: maxY }, end: { x: minX, y: minY } }  // left
  ];

  // Check intersection with each edge
  for (const edge of rectEdges) {
    if (doLinesIntersect(wallStart, wallEnd, edge.start, edge.end)) {
      return true;
    }
  }

  return false;
}

/**
 * Normalizes a highlight area so startPoint is top-left and endPoint is bottom-right
 */
export function normalizeHighlight(highlight: HighlightArea): HighlightArea {
  return {
    ...highlight,
    startPoint: {
      x: Math.min(highlight.startPoint.x, highlight.endPoint.x),
      y: Math.min(highlight.startPoint.y, highlight.endPoint.y)
    },
    endPoint: {
      x: Math.max(highlight.startPoint.x, highlight.endPoint.x),
      y: Math.max(highlight.startPoint.y, highlight.endPoint.y)
    }
  };
}

/**
 * Draws dimension labels (width and length) for highlight preview
 * 🆕 FEATURE: Shows dimensions in meters during preview mode only
 */
function drawHighlightDimensions(
  ctx: CanvasRenderingContext2D,
  highlight: HighlightArea,
  scale: number
): void {
  const PIXELS_PER_METER = 100;
  
  // Calculate rectangle bounds
  const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
  const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
  const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
  const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);
  
  const width = maxX - minX;
  const height = maxY - minY;
  
  // Convert to meters
  const widthMeters = (width / PIXELS_PER_METER).toFixed(2);
  const heightMeters = (height / PIXELS_PER_METER).toFixed(2);
  
  ctx.save();
  
  // Dimension label styling
  const fontSize = Math.max(14, 16 / scale); // Scale-responsive font size
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  
  // Padding for labels
  const padding = 6 / scale;
  const offset = 15 / scale; // Distance from rectangle edge
  
  // Width label (centered above the rectangle)
  const widthText = `${widthMeters}m`;
  const widthLabelX = minX + width / 2;
  const widthLabelY = minY - offset;
  
  // Measure text for background
  const widthMetrics = ctx.measureText(widthText);
  const widthBgWidth = widthMetrics.width + padding * 2;
  const widthBgHeight = fontSize + padding * 2;
  
  // Draw width label background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.fillRect(
    widthLabelX - widthBgWidth / 2,
    widthLabelY - widthBgHeight / 2,
    widthBgWidth,
    widthBgHeight
  );
  
  // Draw width label text
  ctx.fillStyle = '#FFFFFF';
  ctx.fillText(widthText, widthLabelX, widthLabelY);
  
  // Height/Length label (centered to the right of the rectangle)
  const heightText = `${heightMeters}m`;
  const heightLabelX = maxX + offset;
  const heightLabelY = minY + height / 2;
  
  // Measure text for background
  const heightMetrics = ctx.measureText(heightText);
  const heightBgWidth = heightMetrics.width + padding * 2;
  const heightBgHeight = fontSize + padding * 2;
  
  // Draw height label background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.fillRect(
    heightLabelX - heightBgWidth / 2,
    heightLabelY - heightBgHeight / 2,
    heightBgWidth,
    heightBgHeight
  );
  
  // Draw height label text
  ctx.fillStyle = '#FFFFFF';
  ctx.fillText(heightText, heightLabelX, heightLabelY);
  
  // Draw dimension lines
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.lineWidth = 1.5 / scale;
  ctx.setLineDash([]);
  
  // Width dimension line (above rectangle)
  ctx.beginPath();
  ctx.moveTo(minX, minY - offset / 2);
  ctx.lineTo(minX, minY - offset * 1.5);
  ctx.moveTo(minX, minY - offset);
  ctx.lineTo(maxX, minY - offset);
  ctx.moveTo(maxX, minY - offset / 2);
  ctx.lineTo(maxX, minY - offset * 1.5);
  ctx.stroke();
  
  // Height dimension line (right of rectangle)
  ctx.beginPath();
  ctx.moveTo(maxX + offset / 2, minY);
  ctx.lineTo(maxX + offset * 1.5, minY);
  ctx.moveTo(maxX + offset, minY);
  ctx.lineTo(maxX + offset, maxY);
  ctx.moveTo(maxX + offset / 2, maxY);
  ctx.lineTo(maxX + offset * 1.5, maxY);
  ctx.stroke();
  
  ctx.restore();
} 